/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {AsyncButtonEl} from 'common/components/async-button';
import {BannerEl} from 'common/components/banner';
import {BannerType} from 'common/components/banner/types';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {ButtonBarEl} from 'common/components/button-bar';
import {SelectEl} from 'common/components/select';
import {ToggleEl} from 'common/components/toggle';
import {ToggleSize, ToggleTheme} from 'common/components/toggle/types';
import {DisplayType} from 'common/components/tooltip/types';
import {ControlBarEl} from 'common/widgets/control-bar';
import {ScrollControlEl} from 'common/widgets/scroll-control';

import {CodeTipEl} from '../components/code-tip';
import type {DesignSystem} from '../index';
import toggleLocation from '../../assets/toggle-location.png';
import toggleNesting1 from '../../assets/toggle-nesting1.png';
import toggleNesting2 from '../../assets/toggle-nesting2.png';
import toggleColors from '../../assets/toggle-colors.png';
import {Section, SubSection} from './utils';

export function ButtonsSection({designSystem}: {designSystem: DesignSystem}) {
  return (
    <Section title='Buttons'>
      <SubSection title='mp-button' autoDoc={true}>
        <p sel='.desc'>
          Buttons provide cues for actions and events. These fundamental components allow users to process actions or
          navigate an experience. You can change the primary theme colors with css variables --primary-default,
          --primary-hover, --primary-active. Mixpanel's buttons have a built in hierarchy:{` `}
          <strong>{`primary > secondary > tertiary`}</strong>
        </p>

        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Primary}}>Primary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary}}>Secondary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Tertiary}}>Tertiary</ButtonEl>
        </CodeTipEl>

        <h3 sel='.variants'>Variants</h3>
        <p>With icon</p>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Primary, icon: `new-report`}}>Primary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary, icon: `new-report`}}>Secondary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Tertiary, icon: `new-report`}}>Tertiary</ButtonEl>
        </CodeTipEl>

        <p>Icon only</p>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Primary, icon: `plus`}} />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary, icon: `plus`}} />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Tertiary, icon: `plus`}} />
        </CodeTipEl>

        <p>Disabled states (note: this can extend across all variants)</p>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Primary, disabled: true}}>Primary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary, disabled: true}}>Secondary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Tertiary, disabled: true}}>Tertiary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary, disabled: true, icon: `spinner`}}>Saving</ButtonEl>
        </CodeTipEl>

        <p>Destructive (note: this can extend across icon and icon only variants)</p>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Primary, type: ButtonType.Destructive}}>Primary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary, type: ButtonType.Destructive}}>Secondary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Tertiary, type: ButtonType.Destructive}}>Tertiary</ButtonEl>
        </CodeTipEl>

        <p>Success (note: this can extend across icon and icon only variants)</p>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Primary, type: ButtonType.Success}}>Primary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary, type: ButtonType.Success}}>Secondary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Tertiary, type: ButtonType.Success}}>Tertiary</ButtonEl>
        </CodeTipEl>

        <p>Warn (note: this can extend across icon and icon only variants)</p>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Primary, type: ButtonType.Warn}}>Primary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary, type: ButtonType.Warn}}>Secondary</ButtonEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Tertiary, type: ButtonType.Warn}}>Tertiary</ButtonEl>
        </CodeTipEl>

        <p>mp-async-button</p>
        <CodeTipEl sel='.example'>
          <AsyncButtonEl
            params={{
              asyncAction: () => new Promise((r) => setTimeout(r, 2000)),
              theme: ButtonTheme.Primary,
              icon: `new-report`,
            }}
          >
            Process for 2s
          </AsyncButtonEl>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-button-bar' autoDoc={true}>
        <p sel='.desc'>
          Button bars could be used with ui-cards (modals and dialogs) to lead the user through a linear flow or to
          complete a form.
        </p>

        <CodeTipEl sel='.example'>
          <ButtonBarEl sel='.mp-button-bar-secondary'>I'm a button bar</ButtonBarEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonBarEl sel='.mp-button-bar-secondary' attrs={{disabled: true}}>
            I'm a disabled button bar
          </ButtonBarEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonBarEl sel='.mp-button-bar-primary' attrs={{'arrow-next': true}}>
            Next step
          </ButtonBarEl>
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ButtonBarEl sel='.mp-button-bar-primary' attrs={{'arrow-next': true, disabled: true}}>
            Next step
          </ButtonBarEl>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-control-bar' autoDoc={true}>
        <p sel='.desc'>
          An optionionated group of controls that allows for truncation. Used mainly for report headers.
        </p>

        <p>No truncation</p>
        <CodeTipEl sel='.example'>
          <ControlBarEl
            attrs={{
              theme: ButtonTheme.Tertiary,
              controls: JSON.stringify({
                items: [
                  {
                    value: `save`,
                    label: `Save`,
                    icon: `save`,
                  },
                  {
                    value: `duplicate`,
                    label: `Save new`,
                    icon: `duplicate`,
                  },
                  {
                    value: `open`,
                    label: `Open`,
                    icon: `open`,
                    tooltip: {
                      text: `Open Report`,
                    },
                  },
                ],
              }),
            }}
          />
        </CodeTipEl>

        <p>With truncation</p>
        <CodeTipEl sel='.example'>
          <ControlBarEl
            attrs={{
              theme: ButtonTheme.Tertiary,
              controls: JSON.stringify({
                items: [
                  {
                    value: `open`,
                    label: `Open`,
                    icon: `open`,
                  },
                  {
                    value: `refresh`,
                    label: `Refresh`,
                    icon: `refresh`,
                  },
                  {
                    value: `save`,
                    label: `Save`,
                    icon: `save`,
                  },
                  {
                    value: `duplicate`,
                    label: `Save new`,
                    icon: `duplicate`,
                    truncate: true,
                  },
                  {
                    value: `refresh`,
                    label: `Refresh`,
                    icon: `refresh`,
                    truncate: true,
                  },
                ],
              }),
            }}
          />
        </CodeTipEl>

        <p>Using multi-slots for custom render</p>
        <CodeTipEl sel='.example'>
          <ControlBarEl
            attrs={{
              theme: ButtonTheme.Tertiary,
              controls: JSON.stringify({
                items: [
                  {
                    value: `open`,
                    label: `Open`,
                    icon: `open`,
                  },
                  {
                    value: `refresh`,
                    label: `Refresh`,
                    icon: `refresh`,
                  },
                  {
                    slot: `test-slot`,
                    value: `test`,
                    label: `Test`,
                  },
                  {
                    slot: `report-slot`,
                    value: `report`,
                    label: `Report`,
                  },
                ],
              }),
            }}
          >
            <div
              attrs={{
                slot: `report-slot`,
                'project-id': 3,
              }}
            />
            <div
              attrs={{
                slot: `test-slot`,
              }}
            >
              A Slot
            </div>
          </ControlBarEl>
        </CodeTipEl>

        <p>Kitchen sink</p>
        <CodeTipEl sel='.example'>
          <ControlBarEl
            attrs={{
              theme: ButtonTheme.Tertiary,
              controls: {
                items: [
                  {
                    value: `save`,
                    label: `Save`,
                    icon: `save`,
                  },
                  {
                    value: `save_new`,
                    label: `Save as new`,
                  },
                  {
                    value: `open`,
                    label: `Open`,
                    icon: `open`,
                  },
                ],
              },
              'overflow-controls': [
                {
                  items: [
                    {
                      value: `alerts`,
                      label: `Alerts`,
                      icon: `alert-normal`,
                    },
                    {
                      value: `export_csv`,
                      label: `Export CSV`,
                      icon: `export-csv`,
                      truncate: true,
                    },
                    {
                      value: `edit`,
                      label: `Edit Report`,
                      icon: `compose`,
                      truncate: true,
                    },
                    {
                      value: `export_png`,
                      label: `Export PNG`,
                      icon: `export-png`,
                      truncate: true,
                    },
                    {
                      value: `export_PDF`,
                      label: `Export PDF`,
                      icon: `export-pdf`,
                      isDisabled: true,
                      tooltip: {
                        text: `Unable to create PDF from this report`,
                        type: DisplayType.Error,
                      },
                      truncate: true,
                    },
                    {
                      value: `refresh`,
                      label: `Refresh`,
                      icon: `refresh`,
                      truncate: true,
                    },
                  ],
                },
              ],
            }}
            on={{
              select: designSystem.helpers.handleControlBarSelect,
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-toggle' autoDoc={true}>
        <p sel='.desc'>
          Toggles are used for both segmented controllers and tab bars.
          <span sel='.strong'>Segmented Controllers</span> help reararrange and showcase information in various ways.
          This can mean information filtering, sorting or literal view changes.
          <span sel='.strong'>Tab bars</span> enable you to relate multiple navigation endpoints through a single object
          structure. This helps establish a sense of relativity between potentially wildly disparate views.
        </p>
        <h3>Types</h3>
        <h4>Text Only</h4>
        <CodeTipEl sel='.example'>
          <ToggleEl
            attrs={{
              theme: ToggleTheme.SegmentedController,
              selected: `option2`,
              options: [
                {label: `Option 1`, value: `option1`},
                {label: `Option 2`, value: `option2`},
                {label: `Option 3`, value: `option3`},
              ],
            }}
          />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ToggleEl
            attrs={{
              theme: ToggleTheme.TabBar,
              selected: `option2`,
              options: [
                {label: `Option 1`, value: `option1`},
                {label: `Option 2`, value: `option2`},
                {label: `Option 3`, value: `option3`},
              ],
            }}
          />
        </CodeTipEl>
        <h4>Icon with Text</h4>
        <CodeTipEl sel='.example'>
          <ToggleEl
            attrs={{
              theme: ToggleTheme.SegmentedController,
              selected: `option2`,
              options: [
                {label: `Option 1`, value: `option1`, icon: `event`},
                {label: `Option 2`, value: `option2`, icon: `profile`},
                {label: `Option 3`, value: `option3`, icon: `idea`},
              ],
            }}
          />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ToggleEl
            attrs={{
              theme: ToggleTheme.TabBar,
              selected: `option2`,
              options: [
                {label: `Option 1`, value: `option1`, icon: `event`},
                {label: `Option 2`, value: `option2`, icon: `profile`},
                {label: `Option 3`, value: `option3`, icon: `idea`},
              ],
            }}
          />
        </CodeTipEl>
        <h4>Icon Only</h4>
        <CodeTipEl sel='.example'>
          <ToggleEl
            attrs={{
              theme: ToggleTheme.SegmentedController,
              selected: `option2`,
              options: [
                {label: ``, value: `option1`, icon: `event`},
                {label: ``, value: `option2`, icon: `profile`},
                {label: ``, value: `option3`, icon: `idea`},
              ],
            }}
          />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ToggleEl
            attrs={{
              theme: ToggleTheme.TabBar,
              selected: `option2`,
              options: [
                {label: ``, value: `option1`, icon: `event`},
                {label: ``, value: `option2`, icon: `profile`},
                {label: ``, value: `option3`, icon: `idea`},
              ],
            }}
          />
        </CodeTipEl>
        <div sel='.divider-line' />
        <h3>Related Components</h3>
        <div sel='.example'>
          <h4>Dropdowns</h4>
          <SelectEl
            attrs={{
              sections: [
                {
                  items: [
                    {label: `Option 1`, icon: `event`},
                    {label: `Option 2`, icon: `profile`},
                    {label: `Option 3`, icon: `idea`},
                  ],
                },
              ],
              'selected-option': `option2`,
              'menu-width': 200,
            }}
          />
        </div>
        <div sel='.example'>
          <h4>Segmented Controllers</h4>
          <ToggleEl
            attrs={{
              theme: ToggleTheme.SegmentedController,
              selected: `option2`,
              options: [
                {label: `Option 1`, value: `option1`, icon: `event`},
                {label: `Option 2`, value: `option2`, icon: `profile`},
                {label: `Option 3`, value: `option3`, icon: `idea`},
              ],
            }}
          />
        </div>
        <div sel='.example'>
          <h4>Tab bars</h4>
          <ToggleEl
            attrs={{
              theme: ToggleTheme.TabBar,
              selected: `option2`,
              options: [
                {label: `Option 1`, value: `option1`, icon: `event`},
                {label: `Option 2`, value: `option2`, icon: `profile`},
                {label: `Option 3`, value: `option3`, icon: `idea`},
              ],
            }}
          />
        </div>
        <p sel='.desc'>While related, Segmented Controllers, Tabs, and Dropdowns have different patterns.</p>
        <p sel='.desc'>
          <span sel='.strong'>Segmented Controllers</span> should be used as navigational lenses providing near
          immediate feedback on the same or similar information. Typically the information stays the same, but it’s the
          view that changes. Segmented Controllers may also be used as a selection method replacing a number of radio
          buttons as its function are very similar.
        </p>
        <p sel='.desc'>
          e.g. You open a music player. The music library show artists, albums, and song, but you may want to view it as
          a table view, an album view, or a list view. While these views vary wildly, the informational structure is
          intact and it is the view that changes.
        </p>
        <p sel='.desc'>
          <span sel='.strong'>Tab bars</span> should be used to connect related places, which may lead to multiple pages
          and may result in a page load. Both the place and the information varies.
        </p>
        <p sel='.desc'>
          e.g. You’re within a web browser, but have multiple tabs open. Each tab is a totally different location and
          provides you with no idea of what to expect, but they are related in that they’re all within the browser.
        </p>
        <p sel='.desc'>
          <span sel='.strong'>Dropdowns</span> are useful when the number of items within a Segmented Controller is too
          vast to showcase, or when there are many Segmented Controllers in close proximity. Conversely, when a Dropdown
          only has one or two items contained within their menu, it may be worth swapping with a Segmented Controller.
        </p>
        <div sel='.divider-line' />
        <h3>Examples</h3>
        <h4>Location</h4>
        <BannerEl
          attrs={{
            theme: `success`,
            type: BannerType.Embedded,
            'header-text': `Do`,
            'body-text': `Do feel free to nest Segmented Controllers within a Tab Bar view`,
          }}
        />
        <br />
        <img sel='.example-image' attrs={{src: toggleLocation}} />
        <br />
        <br />
        <h4>Nesting Tab Bars</h4>
        <BannerEl
          attrs={{
            theme: `success`,
            type: BannerType.Embedded,
            'header-text': `Do`,
            'body-text': `Do feel free to use mutiple Tab Bars within a page.`,
          }}
        />
        <br />
        <img sel='.example-image' attrs={{src: toggleNesting1}} />
        <br />
        <BannerEl
          attrs={{
            theme: `error`,
            type: BannerType.Embedded,
            'header-text': `Do Not`,
            'body-text': `Do not stack multiple tab bars within the same container.`,
          }}
        />
        <br />
        <img sel='.example-image' attrs={{src: toggleNesting2}} />
        <br />
        <br />
        <h4>Label Types</h4>
        <BannerEl
          attrs={{
            theme: `error`,
            type: BannerType.Embedded,
            'header-text': `Do Not`,
            'body-text': `Do not use multiple types of labels within the tab bar. Use one throughout the entire bar.`,
          }}
        />
        <br />
        <div sel='.example'>
          <ToggleEl
            attrs={{
              theme: ToggleTheme.SegmentedController,
              size: ToggleSize.Medium,
              options: [
                {value: `all`, label: `All`, icon: `idea`, tooltipLabel: `do not do this`},
                {value: `events`, label: ``, icon: `event`},
                {value: `people`, label: `People`, icon: `profile`},
                {value: `things`, label: `Things`},
                {value: `other`, label: `Other`, icon: `thumbs-down`},
              ],
            }}
          />
        </div>
        <br />
        <br />
        <h4>Label Colors</h4>
        <BannerEl
          attrs={{
            theme: `error`,
            type: BannerType.Embedded,
            'header-text': `Do Not`,
            'body-text': `Do not introduce new color styles. Use the patterns that exist. If you believe your use case requires a new pattern, talk to your design system lead about introducing a new pattern.`,
          }}
        />
        <br />
        <img sel='.example-image' attrs={{src: toggleColors}} />
        <br />
        <div sel='.divider-line' />
        <p>Default - theme: segmented-controller, size: medium</p>
        <CodeTipEl sel='.example'>
          <ToggleEl
            attrs={{
              selected: designSystem.state.blueToggleValue,
              options: [
                {label: `Longer Option 1`, value: `option1`},
                {label: `Option 2`, value: `option2`},
                {label: `Option 3`, value: `option2`, disabled: true},
              ],
            }}
            on={{select: designSystem.helpers.blueToggleChanged}}
          />
        </CodeTipEl>
        <p>tab-bar theme and options with disabled and icon</p>
        <CodeTipEl sel='.example'>
          <ToggleEl
            attrs={{
              theme: ToggleTheme.TabBar,
              selected: `events`,
              options: [
                {value: `all`, label: `All`, icon: `idea`},
                {value: `events`, label: `Events`, icon: `event`},
                {value: `leads`, disabled: true, label: `Leads`, icon: `profile`},
                {value: `accounts`, disabled: true, label: `Accounts`, icon: `coding`},
                {value: `contacts`, label: `Contacts`, icon: `warning`},
              ],
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-scroll-control' autoDoc={true}>
        <p sel='.desc'>Scroll buttons that appear based on mouse position when scrollEl can be scrolled.</p>

        <CodeTipEl sel='.example' hook={{insert: ({elm}) => designSystem.updateElRef({mousemoveEl: elm})}}>
          <ScrollControlEl
            attrs={{
              'scroll-by': 335,
            }}
            props={{
              mousemoveEl: designSystem.elRefs.mousemoveEl,
              scrollEl: designSystem.elRefs.scrollEl,
            }}
          />
          <div sel='.scroll-box' hook={{insert: ({elm}) => designSystem.updateElRef({scrollEl: elm})}}>
            <BannerEl
              attrs={{
                theme: `success`,
                type: BannerType.Embedded,
                'header-text': `Scroll Us!`,
                'body-text': `Scroll buttons will appear as you mouse over.                       `,
              }}
            />
            <BannerEl
              attrs={{
                theme: `info`,
                type: BannerType.Embedded,
                'header-text': `Dev Notes`,
                'body-text': `The "scrollEl" prop is a sibling, while "mousemoveEl" is a parent`,
              }}
            />
            <BannerEl
              attrs={{
                theme: `success`,
                type: BannerType.Embedded,
                'header-text': `Scroll Us!`,
                'body-text': `Scroll buttons will appear as you mouse over.                       `,
              }}
            />
            <BannerEl
              attrs={{
                theme: `info`,
                type: BannerType.Embedded,
                'header-text': `Dev Notes`,
                'body-text': `The "scrollEl" prop is a sibling, while "mousemoveEl" is a parent`,
              }}
            />
          </div>
        </CodeTipEl>
      </SubSection>
    </Section>
  );
}
